import React, { useId, useMemo, useState } from "react"

import {
  useAlertTransportInvalidation,
  useCreateAlertTransport,
} from "@/alerts"
import { useMetricsList, type MetricIdentity } from "@/metrics/catalog"
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { AlertTriangle, Bell } from "lucide-react"

import { logger } from "@/lib/logger"
import type { ConditionOperator } from "@/types_api/backend/transport/create_alert_request"
import {
  CONVERSATION_SOURCE,
  toTransportConversationSourceFilter,
  type ConversationSource,
} from "@/types/conversationSource"
import { FLOAT_COMPARISON_OPERATOR } from "@/types/metrics/contracts"

import type { DashboardWidget } from "../types/core"

const log = logger("AlertFromWidgetModal")

const OPERATOR_MAP: Record<string, ConditionOperator> = {
  [FLOAT_COMPARISON_OPERATOR.GT]: "GT",
  [FLOAT_COMPARISON_OPERATOR.GTE]: "GTE",
  [FLOAT_COMPARISON_OPERATOR.LT]: "LT",
  [FLOAT_COMPARISON_OPERATOR.LTE]: "LTE",
  [FLOAT_COMPARISON_OPERATOR.EQ]: "EQ",
}

export function getAlertConversationSource(
  sources: ConversationSource[],
): "ALL" | "UPLOADED" | "SIMULATED" {
  return toTransportConversationSourceFilter(
    sources.length === 1 ? sources[0] : "all",
  )
}

interface AlertFromWidgetModalProps {
  isOpen: boolean
  onClose: () => void
  widget: DashboardWidget
}

export const AlertFromWidgetModal: React.FC<AlertFromWidgetModalProps> = ({
  isOpen,
  onClose,
  widget,
}) => {
  const invalidateAlerts = useAlertTransportInvalidation()

  const alertNameId = useId()
  const operatorSelectId = useId()
  const thresholdId = useId()
  const uploadedCheckboxId = useId()
  const simulationCheckboxId = useId()

  const [alertName, setAlertName] = useState("")
  const [operator, setOperator] = useState<string>(FLOAT_COMPARISON_OPERATOR.GT)
  const [threshold, setThreshold] = useState<string>("")
  const [runTypes, setRunTypes] = useState<ConversationSource[]>([
    CONVERSATION_SOURCE.UPLOADED,
  ])
  const [isCreating, setIsCreating] = useState(false)

  const metricId = widget.config?.metricId
  const { data: metricRecords } = useMetricsList({
    metricNames: metricId ? [metricId] : undefined,
    enabled: Boolean(metricId),
  })

  const selectedMetric: MetricIdentity | undefined = useMemo(() => {
    if (!metricRecords || !metricId) {
      return undefined
    }
    return Object.values(metricRecords).find((metric) => metric.id === metricId)
  }, [metricRecords, metricId])

  const { mutate: createAlert } = useCreateAlertTransport({
    onSuccess: async () => {
      await invalidateAlerts()
      onClose()
    },
    onError: (error) => {
      log.error("Failed to create alert", error)
    },
    onMutate: () => setIsCreating(true),
    onSettled: () => setIsCreating(false),
  })

  const handleCreate = () => {
    if (!metricId || !threshold) {
      return
    }
    const parsedThreshold = Number.parseFloat(threshold)
    if (!Number.isFinite(parsedThreshold)) {
      return
    }

    const metricDisplayName =
      selectedMetric?.metric_display_name ||
      selectedMetric?.metric_name ||
      metricId

    createAlert({
      name: alertName.trim() || `${metricDisplayName} alert`,
      evaluation_type: "ON_RUN_COMPLETE",
      conversation_source: getAlertConversationSource(runTypes),
      conditions: [
        {
          metric_id: metricId,
          aggregation: "SINGLE",
          operator: OPERATOR_MAP[operator] ?? "GT",
          threshold_float: parsedThreshold,
        },
      ],
      channels: [],
    })
  }

  const handleRunTypeToggle = (type: ConversationSource, checked: boolean) => {
    setRunTypes((prev) =>
      checked ? [...prev, type] : prev.filter((t) => t !== type),
    )
  }

  const operatorOptions = [
    { value: FLOAT_COMPARISON_OPERATOR.GT, label: "Greater than (>)" },
    {
      value: FLOAT_COMPARISON_OPERATOR.GTE,
      label: "Greater than or equal (≥)",
    },
    { value: FLOAT_COMPARISON_OPERATOR.LT, label: "Less than (<)" },
    { value: FLOAT_COMPARISON_OPERATOR.LTE, label: "Less than or equal (≤)" },
    { value: FLOAT_COMPARISON_OPERATOR.EQ, label: "Equal to (=)" },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              <span>Create Alert</span>
            </div>
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-6">
            <div>
              <Label htmlFor={alertNameId}>Alert Name</Label>
              <Input
                id={alertNameId}
                placeholder={`${selectedMetric?.metric_display_name || metricId || "Metric"} alert`}
                value={alertName}
                onChange={(e) => setAlertName(e.target.value)}
              />
            </div>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold">Metric</h4>
                    <p className="text-sm text-coval-text-muted">
                      {selectedMetric?.metric_display_name ||
                        metricId ||
                        "Unknown Metric"}
                    </p>
                  </div>
                  <Badge>{widget.type}</Badge>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Condition</h4>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <Label htmlFor={operatorSelectId}>When value is</Label>
                  <Select value={operator} onValueChange={setOperator}>
                    <SelectTrigger id={operatorSelectId}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {operatorOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label htmlFor={thresholdId}>Value</Label>
                  <Input
                    id={thresholdId}
                    type="number"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    step="any"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Scope</h4>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={uploadedCheckboxId}
                    checked={runTypes.includes(CONVERSATION_SOURCE.UPLOADED)}
                    onCheckedChange={(checked) =>
                      handleRunTypeToggle(
                        CONVERSATION_SOURCE.UPLOADED,
                        !!checked,
                      )
                    }
                  />
                  <Label htmlFor={uploadedCheckboxId}>Uploaded</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={simulationCheckboxId}
                    checked={runTypes.includes(CONVERSATION_SOURCE.SIMULATED)}
                    onCheckedChange={(checked) =>
                      handleRunTypeToggle(
                        CONVERSATION_SOURCE.SIMULATED,
                        !!checked,
                      )
                    }
                  />
                  <Label htmlFor={simulationCheckboxId}>Simulated</Label>
                </div>
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isCreating || !threshold || runTypes.length === 0}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            {isCreating ? "Creating..." : "Create Alert"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
